import { captureException } from '@sentry/react'
import { HttpError } from 'app/rest'
import { makeAutoObservable, runInAction } from 'mobx'
import { api } from 'store/api'
import { AvatarType } from 'type/Avatar'
import { FullError, getFullError, UserError, UserErrorType } from 'util/error'
import { fetchRetry } from 'util/fetch'
import { processImageFile } from 'util/image'
import { getQuery, Query, replaceQuery } from 'util/url'

type SelfieStoreOptions = {
  fullbody?: boolean
}

function detectSelfieErrorType(error: any): UserErrorType {
  if (error instanceof UserError) return error.type
  if (error instanceof HttpError) {
    // const message = error.json.message || ''
    if (error.status === 400) return 'bad_selfie'
    // 413 отдаёт наш сервер, когда фото не влезает в лимит загрузки.
    if (error.status === 413) return 'too_large_file'
  }
  return 'upload_failed'
}

export class SelfieStore {
  private file?: File
  private asset?: string

  origin?: 'asset' | 'file' | 'url'
  src?: string
  private _url?: string
  err?: FullError
  promise?: Promise<string | undefined>
  rotation: number = 0
  totalRotation: number = 0
  avatarType?: AvatarType
  private fullbody: boolean = false
  query: Query = {}

  constructor(options: SelfieStoreOptions = {}) {
    makeAutoObservable(this)
    this.fullbody = options.fullbody ?? false
  }

  set measurements(measurements: boolean) {
    if (measurements) this.query.measurements = ''
    else delete this.query.measurements
  }

  get url(): string {
    return replaceQuery(this._url, this.query) || ''
  }

  get empty(): boolean {
    return !this.origin
  }

  get uploading(): boolean {
    return !!this.promise
  }

  get error(): string {
    return this.err?.title || ''
  }

  getUrlPromise() {
    return this.promise?.catch(() => undefined) ?? Promise.resolve(this.url)
  }

  async setFile(file: File, type?: AvatarType) {
    this.reset(true)
    delete this.query.asset
    this.avatarType = type ?? 'camera'
    this.origin = 'file'
    this.file = file
    this.src = URL.createObjectURL(file)
    this.promise = this.uploadFile(file)
    await this.promise
  }

  async setAsset(asset: string, name?: string) {
    this.reset(true)
    this.query.asset = name || ''
    this.avatarType = 'preset'
    this.origin = 'asset'
    this.asset = asset
    this.src = asset
    this.promise = this.uploadAsset(asset)
    await this.promise
  }

  setUrl(url: string | undefined) {
    this.reset(true)
    if (!url) return
    this.origin = 'url'
    this.src = url
    this.applyUrl(url, true)
  }

  setError(type: UserErrorType | undefined) {
    this.reset(true)
    this.err = type ? getFullError(type) : undefined
  }

  clear(all?: boolean) {
    this.reset(all)
  }

  setRotation(rotation: number) {
    this.rotation = rotation
  }

  addRotation(degrees: number) {
    this.totalRotation += degrees
    this.rotation = this.totalRotation % 360
  }

  async applyRotation() {
    if (this.rotation === 0 || !this.src) return

    try {
      const rotatedDataUrl = await this.rotateImageData(this.src, this.rotation)
      const rotatedFile = this.dataURLtoFile(rotatedDataUrl, 'rotated-photo.jpg')
      await this.setFile(rotatedFile)
      this.rotation = 0
    } catch (error) {
      console.error('rotation failed', error)
      throw error
    }
  }

  private rotateImageData(imageDataUrl: string, rotation: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')!
      const img = new Image()

      img.crossOrigin = 'anonymous'

      img.onload = () => {
        try {
          const rad = (rotation * Math.PI) / 180
          const sin = Math.abs(Math.sin(rad))
          const cos = Math.abs(Math.cos(rad))

          canvas.width = img.width * cos + img.height * sin
          canvas.height = img.width * sin + img.height * cos

          ctx.translate(canvas.width / 2, canvas.height / 2)
          ctx.rotate(rad)
          ctx.drawImage(img, -img.width / 2, -img.height / 2)

          resolve(canvas.toDataURL('image/jpeg', 0.9))
        } catch (error) {
          reject(error)
        }
      }

      img.onerror = () => reject(new Error('Failed to load image'))
      img.src = imageDataUrl
    })
  }

  private dataURLtoFile(dataurl: string, filename: string): File {
    const arr = dataurl.split(',')
    const mimeMatch = arr[0]?.match(/:(.*?);/)
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg'
    const base64Data = arr[1]
    if (!base64Data) throw new Error('Invalid data URL')

    const bstr = atob(base64Data)
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new File([u8arr], filename, { type: mime })
  }

  private async uploadFile(raw: File): Promise<string | undefined> {
    try {
      const file = await processImageFile(raw)
      const url = await this.upload(file)
      if (this.file === raw) runInAction(() => {
        this.file = undefined
        this.promise = undefined
        this.src = url || undefined
        this.applyUrl(url)
      })
      return url
    } catch (error) {
      if (this.file === raw) this.catchError(error)
    }
  }

  private async uploadAsset(asset: string): Promise<string | undefined> {
    try {
      const s3 = asset.includes('.s3.eu-central-1.amazonaws.com')
      let url = ''
      if (s3) {
        await Promise.resolve()
        url = asset
      } else {
        const res: Response = await fetchRetry(asset)
        const blob: Blob = await res.blob()
        let file = new File([blob], 'image.jpg', { type: blob.type })
        file = await processImageFile(file)
        url = await this.upload(file)
      }
      if (this.asset === asset) runInAction(() => {
        this.asset = undefined
        this.promise = undefined
        this.src = url || undefined
        this.applyUrl(url)
      })
      return url
    } catch (error) {
      if (this.asset === asset) this.catchError(error)
    }
  }

  private async upload(file: File) {
    const url = this.fullbody
      ? await api.uploadFullbody(file)
      : await api.uploadSelfie(file)
    return url
  }

  private applyUrl(url: string | undefined, reset?: boolean) {
    if (reset) this.query = getQuery(url)
    this._url = url || undefined
  }

  private catchError(e: unknown) {
    console.error('upload failed', e)
    captureException(e)
    const type = detectSelfieErrorType(e)
    this.setError(type)
  }

  private reset(all?: boolean) {
    if (this.src) URL.revokeObjectURL(this.src)
    this.promise = undefined
    this.file = undefined
    this.asset = undefined
    this.origin = undefined
    this.src = undefined
    this.applyUrl(undefined)
    if (all) this.err = undefined
    this.rotation = 0
    this.totalRotation = 0
  }
}
